import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { Pipeline } from '@orgus/protocol';
import { RingMeter, RunStrip, StatStrip, StatTile, StateBar, formatInt, formatPercent, plural } from '@orgus/ui';
import { recentRuns, runSummary } from '../overview/pulse.ts';
import type { ProjectWork } from './work.ts';

/**
 * Where work stands across every project, in four numbers with their figures: what waits on
 * you, what runs, what is in the landing pipeline, and what landed. Grey by default; only
 * Needs you turns amber, and only when something does.
 */
export function ProjectsGlance({ work, pipelines }: { work: ProjectWork[]; pipelines: Pipeline[] }) {
  const navigate = useNavigate();
  const sum = (f: (w: ProjectWork) => number) => work.reduce((a, w) => a + f(w), 0);
  const needs = sum((w) => w.counts['needs-you']);
  const failed = sum((w) => w.counts.failed);
  const running = sum((w) => w.counts.running);
  const idle = sum((w) => w.counts.stale);
  const queued = sum((w) => w.counts.queued);
  const verifying = sum((w) => w.counts.verifying);
  const landed = sum((w) => w.counts.landed);
  const needsIn = work.filter((w) => w.counts['needs-you'] > 0).length;
  const runningIn = work.filter((w) => w.counts.running > 0).length;
  const runs = useMemo(() => recentRuns(pipelines), [pipelines]);
  const finished = landed + work.reduce((a, w) => a + w.items.filter((i) => i.kind === 'landing' && i.state === 'failed').length, 0);

  return (
    <StatStrip label="Work across projects" className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        label="Needs you"
        value={needs}
        attention={needs > 0}
        sub={needs ? `In ${plural(needsIn, 'project')}${failed ? `, ${formatInt(failed)} failed` : ''}` : failed ? `${formatInt(failed)} failed` : 'Nothing needs you'}
        visual={<StateBar counts={{ 'needs-you': needs, failed }} />}
        onOpen={() => navigate('/inbox')}
      />
      <StatTile
        label="Running"
        value={running}
        sub={running ? `In ${plural(runningIn, 'project')}, ${formatInt(idle)} idle` : `${plural(idle, 'idle checkout')}`}
        visual={<StateBar counts={{ running, stale: idle }} />}
        onOpen={() => navigate('/sessions')}
      />
      <StatTile
        label="In the pipeline"
        value={queued + verifying}
        sub={queued + verifying ? `${formatInt(verifying)} verifying, ${formatInt(queued)} queued` : 'Queue empty'}
        visual={<RunStrip runs={runs} slots={24} label={`Recent landing runs: ${runSummary(runs)}`} />}
        onOpen={() => navigate('/pipelines')}
      />
      <StatTile
        label="Landed"
        featured
        value={landed}
        unit="in 24 hours"
        sub={finished ? `${formatPercent(landed / finished)} of finished runs` : 'No finished runs'}
        trend={<RingMeter value={finished ? landed / finished : 0} size={40} tone={3} label={`${formatInt(landed)} of ${formatInt(finished)} finished runs landed`} />}
      />
    </StatStrip>
  );
}
